<template>
  <div class="tools-tab">
    <!-- Offerte informatie -->
    <section class="ds-card">
      <div class="ds-card__header">
        <ds-icon name="doc" />
        Offerte informatie
      </div>
      <div class="ds-card__body">
        <div class="tools-tab__searchbar">
          <div class="ds-form__field">
            <label for="quote-id">Offertenr.</label>
            <input
              id="quote-id"
              v-model="quoteId"
              type="text"
              inputmode="numeric"
              pattern="\d*"
              class="ds-field"
              placeholder="Voer een offertenummer in"
              @input="quoteId = $event.target.value.replace(/\D/g, '')"
              @keyup.enter="searchQuote"
            />
          </div>
          <ds-button
            data-qa="tools-quote-search-button"
            :disabled="!quoteId || quoteLoading"
            @click="searchQuote"
          >
            <ds-icon name="search" />
            {{ quoteLoading ? 'Laden…' : 'Zoeken' }}
          </ds-button>
        </div>

        <div v-if="quoteError" class="ds-alert ds-alert--danger">
          <alert-icon />
          <strong>{{ quoteError }}</strong>
        </div>
        <div v-else-if="quoteResult">
          <div v-for="(value, key) in quoteDisplayFields" :key="key" class="ds-row ds-row--2col">
            <span class="ds-row__label">{{ key }}</span>
            <span class="ds-row__value">{{ value ?? '-' }}</span>
          </div>
          <div
            v-if="quoteHasCommissionNumber || quoteHasConfigurationCode"
            class="tools-tab__result-actions"
          >
            <ds-button
              v-if="quoteHasCommissionNumber"
              data-qa="tools-view-stock-car-button"
              @click="openStockCar(quoteResult)"
            >
              Bekijk voorraadauto
            </ds-button>
            <ds-button
              v-if="quoteHasConfigurationCode"
              variant="ghost"
              data-qa="tools-view-configuration-button"
              @click="openConfiguration(quoteResult)"
            >
              Bekijk configuratie
            </ds-button>
          </div>
        </div>
        <div v-else-if="quoteSearched" class="ds-alert ds-alert--warning">
          <alert-icon />
          <strong>Geen offerte gevonden.</strong>
        </div>
      </div>
    </section>

    <!-- Voorraadauto informatie -->
    <section class="ds-card">
      <div class="ds-card__header">
        <ds-icon name="car" />
        Voorraadauto informatie
      </div>
      <div class="ds-card__body">
        <div class="tools-tab__searchbar">
          <div class="ds-form__field">
            <label for="commission-number">Komnummer</label>
            <input
              id="commission-number"
              v-model="commissionNumber"
              type="text"
              inputmode="numeric"
              pattern="\d*"
              class="ds-field"
              placeholder="Voer een komnummer in"
              @input="commissionNumber = $event.target.value.replace(/\D/g, '')"
              @keyup.enter="searchStock"
            />
          </div>
          <ds-button
            data-qa="tools-stock-search-button"
            :disabled="!commissionNumber || stockLoading"
            @click="searchStock"
          >
            <ds-icon name="search" />
            {{ stockLoading ? 'Laden…' : 'Zoeken' }}
          </ds-button>
        </div>

        <div v-if="stockError" class="ds-alert ds-alert--danger">
          <alert-icon />
          <strong>{{ stockError }}</strong>
        </div>

        <template v-else-if="stockResults && stockResults.length > 0">
          <div v-for="(row, idx) in stockResults" :key="idx" class="ds-card tools-tab__stock-card">
            <div>
              <div v-for="col in stockDisplayColumns" :key="col.key" class="ds-row ds-row--2col">
                <span class="ds-row__label">{{ col.label }}</span>
                <span class="ds-row__value">
                  {{ col.format ? col.format(row[col.key]) : (row[col.key] ?? '-') }}
                </span>
              </div>
            </div>
            <div class="ds-card__footer tools-tab__stock-actions">
              <ds-button size="sm" data-qa="tools-open-stock-car-button" @click="openStockCar(row)">
                Open
              </ds-button>
              <ds-button
                variant="ghost"
                size="sm"
                data-qa="tools-replace-with-baseline-button"
                :disabled="replacingIndex === idx"
                @click="confirmReplace(row, idx)"
              >
                {{ replacingIndex === idx ? 'Bezig…' : 'Vervangen met baseline' }}
              </ds-button>
            </div>
          </div>

          <div v-if="replaceSuccess" class="ds-alert ds-alert--info tools-tab__replace-message">
            <strong>{{ replaceSuccess }}</strong>
          </div>
          <div v-if="replaceError" class="ds-alert ds-alert--danger tools-tab__replace-message">
            <alert-icon />
            <strong>{{ replaceError }}</strong>
          </div>
        </template>

        <div v-else-if="stockSearched" class="ds-alert ds-alert--warning">
          <alert-icon />
          <strong>Geen resultaten gevonden.</strong>
        </div>
      </div>
    </section>

    <!-- Dealer accessoires -->
    <section class="ds-card">
      <div class="ds-card__header">
        <ds-icon name="tag" />
        Dealer accessoires
      </div>
      <div class="ds-card__body">
        <div class="tools-tab__accbar">
          <dealer-combobox
            id="accessories-dealer"
            v-model="accessoriesDealerId"
            class="tools-tab__accbar-combobox"
            label="Dealer"
          />

          <div class="ds-form__field tools-tab__accbar-mode">
            <label>Zoeken op</label>
            <div class="ds-segmented" role="radiogroup" aria-label="Zoeken op">
              <button
                type="button"
                class="ds-segmented__option"
                :class="{
                  'ds-segmented__option--active': accessoriesSearchMode === 'configurationCode',
                }"
                role="radio"
                :aria-checked="accessoriesSearchMode === 'configurationCode'"
                @click="accessoriesSearchMode = 'configurationCode'"
              >
                Configuratiecode
              </button>
              <button
                type="button"
                class="ds-segmented__option"
                :class="{ 'ds-segmented__option--active': accessoriesSearchMode === 'stockId' }"
                role="radio"
                :aria-checked="accessoriesSearchMode === 'stockId'"
                @click="accessoriesSearchMode = 'stockId'"
              >
                Komnummer
              </button>
            </div>
          </div>

          <div class="ds-form__field">
            <label for="accessories-value">
              {{ accessoriesSearchMode === 'configurationCode' ? 'Configuratiecode' : 'Komnummer' }}
            </label>
            <input
              id="accessories-value"
              v-model="accessoriesSearchValue"
              type="text"
              class="ds-field"
              :placeholder="
                accessoriesSearchMode === 'configurationCode' ? 'Configuratiecode' : 'Komnummer'
              "
              @input="onAccessoriesValueInput"
              @keyup.enter="searchDealerAccessories"
            />
          </div>
          <ds-button
            data-qa="tools-accessories-search-button"
            :disabled="!canSearchAccessories || accessoriesLoading"
            @click="searchDealerAccessories"
          >
            <ds-icon name="search" />
            {{ accessoriesLoading ? 'Laden…' : 'Zoeken' }}
          </ds-button>
        </div>

        <div v-if="accessoriesError" class="ds-alert ds-alert--warning">
          <alert-icon />
          <strong>{{ accessoriesError }}</strong>
        </div>
        <dealer-accessories-grid
          v-else-if="accessoriesResults && accessoriesResults.length > 0"
          :items="accessoriesResults"
        />
        <div v-else-if="accessoriesSearched" class="ds-alert ds-alert--warning">
          <alert-icon />
          <strong>Geen accessoires gevonden.</strong>
        </div>
      </div>
    </section>

    <ds-confirm-dialog
      id="tools-replace-baseline"
      :show="!!replaceConfirmRow"
      title="Vervangen met baseline"
      :body="replaceConfirmText"
      confirm-label="Bevestigen"
      cancel-label="Annuleren"
      :confirm-disabled="replacingIndex !== null"
      @confirm="executeReplace"
      @cancel="replaceConfirmRow = null"
    />
  </div>
</template>

<script>
import { h } from 'vue'
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import DsIcon from '@/components/ds/atoms/dsIcon/DsIcon.vue'
import DsConfirmDialog from '@/components/ds/molecules/dsConfirmDialog/DsConfirmDialog.vue'
import DealerAccessoriesGrid from './DealerAccessoriesGrid.vue'
import DealerCombobox from '@/components/generic/presentational/molecules/dealerCombobox/DealerCombobox.vue'
import adminApi from '@/api/server/Admin.api'
import DealerAccessories from '@/api/server/DealerAccessory.api'
import { getBrandNameByCode, getBrandUrlByCode } from '@/helpers/BrandHelper'
import brands from '@/helpers/enums/brands'

// The warning-triangle glyph every alert in this tab leads with.
const AlertIcon = {
  name: 'AlertIcon',
  render() {
    return h(
      'svg',
      { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': 'true' },
      [
        h('path', {
          d: 'M12 9v4M12 17h.01',
          stroke: 'currentColor',
          'stroke-width': 2,
          'stroke-linecap': 'round',
        }),
        h('circle', { cx: 12, cy: 12, r: 9, stroke: 'currentColor', 'stroke-width': 1.6 }),
      ]
    )
  },
}

const BRAND_FIELD_KEYS = ['Brand', 'brand', 'StockBrand', 'stockBrand']

const FIELD_LABELS = {
  dealerid: 'Dealernummer',
  establishmentid: 'Vestiging',
  employeeponid: 'Medewerker',
  employeeautolineid: 'Autoline ID',
  configurationcode: 'Configuratiecode',
  commissionnumber: 'Komnummer',
  commissionyear: 'Komjaar',
  stockid: 'Voorraad ID',
  quoteid: 'Offertenr.',
  status: 'Status',
  orderstatusdescription: 'Orderstatus',
  description: 'Omschrijving',
  licenseplatenumber: 'Kenteken',
  orderedbyquoteid: 'Offertenr. bestelling',
}

const getFieldLabel = (key) => FIELD_LABELS[key.toLowerCase()] || key

const ZERO_AS_EMPTY_FIELDS = ['commissionnumber', 'commissionyear']

const formatFieldValue = (key, value) => {
  if (ZERO_AS_EMPTY_FIELDS.includes(key.toLowerCase()) && (value === 0 || value === '0')) {
    return '-'
  }
  return value
}

export default {
  name: 'ToolsTab',
  components: {
    AlertIcon,
    DsButton,
    DsIcon,
    DsConfirmDialog,
    DealerAccessoriesGrid,
    DealerCombobox,
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,

      quoteId: null,
      quoteResult: null,
      quoteLoading: false,
      quoteError: null,
      quoteSearched: false,

      commissionNumber: '',
      stockResults: null,
      stockLoading: false,
      stockError: null,
      stockSearched: false,

      replacingIndex: null,
      replaceSuccess: null,
      replaceError: null,
      replaceConfirmRow: null,
      replaceConfirmIdx: null,

      accessoriesDealerId: '',
      accessoriesSearchMode: 'configurationCode',
      accessoriesSearchValue: '',
      accessoriesResults: null,
      accessoriesLoading: false,
      accessoriesError: null,
      accessoriesSearched: false,
    }
  },
  computed: {
    quoteHasCommissionNumber() {
      if (!this.quoteResult) return false
      const cn = this.quoteResult.CommissionNumber || this.quoteResult.commissionNumber || ''
      return cn !== ''
    },
    quoteHasConfigurationCode() {
      if (!this.quoteResult) return false
      const cc = this.quoteResult.ConfigurationCode || this.quoteResult.configurationCode || null
      return cc !== null && cc !== ''
    },
    quoteDisplayFields() {
      if (!this.quoteResult) return {}
      const fields = {}
      for (const [key, value] of Object.entries(this.quoteResult)) {
        if (BRAND_FIELD_KEYS.includes(key)) {
          fields['Merk'] = getBrandNameByCode(value)
        } else {
          fields[getFieldLabel(key)] = formatFieldValue(key, value)
        }
      }
      return fields
    },
    stockDisplayColumns() {
      if (!this.stockResults || this.stockResults.length === 0) return []
      return Object.keys(this.stockResults[0]).map((key) => {
        if (BRAND_FIELD_KEYS.includes(key)) {
          return { key, label: 'Merk', format: getBrandNameByCode }
        }
        return { key, label: getFieldLabel(key) }
      })
    },
    replaceConfirmText() {
      if (!this.replaceConfirmRow) return ''
      const brand = this.replaceConfirmRow.Brand || this.replaceConfirmRow.brand || ''
      const commissionNumber =
        this.replaceConfirmRow.CommissionNumber || this.replaceConfirmRow.commissionNumber || ''
      return `Weet je zeker dat je ${getBrandNameByCode(brand)} voorraadauto ${commissionNumber} wilt vervangen door de MOS baseline?`
    },
    canSearchAccessories() {
      return !!this.accessoriesDealerId && !!this.accessoriesSearchValue
    },
  },
  watch: {
    accessoriesSearchMode() {
      this.accessoriesSearchValue = ''
    },
  },
  methods: {
    async searchQuote() {
      if (!this.quoteId) return
      this.quoteLoading = true
      this.quoteError = null
      this.quoteResult = null
      this.quoteSearched = false
      try {
        const { data } = await adminApi.getQuoteInfo(this.quoteId)
        this.quoteResult = Array.isArray(data) ? data[0] || null : data
        this.quoteSearched = true
      } catch {
        this.quoteError = 'Er is een fout opgetreden bij het ophalen van de offerte informatie.'
      } finally {
        this.quoteLoading = false
      }
    },
    async searchStock() {
      if (!this.commissionNumber) return
      this.stockLoading = true
      this.stockError = null
      this.stockResults = null
      this.stockSearched = false
      this.replaceSuccess = null
      this.replaceError = null
      try {
        const { data } = await adminApi.getStockInfo(this.commissionNumber)
        this.stockResults = data
        this.stockSearched = true
      } catch {
        this.stockError =
          'Er is een fout opgetreden bij het ophalen van informatie over de voorraadauto.'
      } finally {
        this.stockLoading = false
      }
    },
    openStockCar(row) {
      const brandCode = row.Brand || row.brand || ''
      const origin = this.getBrandOrigin(brandCode)
      if (!origin) return

      const commissionNumber = row.CommissionNumber || row.commissionNumber || ''
      const dealerId = this.$store.state.user.dealerId

      const path = `/b2c/vestiging/${dealerId}?tab=0&search=${encodeURIComponent(commissionNumber)}`
      window.open(origin + path, '_blank')
    },
    getBrandOrigin(brandCode) {
      const targetBrandUrl = getBrandUrlByCode(brandCode)
      if (!targetBrandUrl) return null
      const currentBrand = brands.find((b) => window.location.hostname.indexOf(b.urlValue) > -1)
      let origin = window.location.origin
      if (currentBrand && currentBrand.urlValue !== targetBrandUrl) {
        origin = origin.replace(currentBrand.urlValue, targetBrandUrl)
      }
      return origin
    },
    openConfiguration(row) {
      const brandCode = row.Brand || row.brand || ''
      const origin = this.getBrandOrigin(brandCode)
      if (!origin) return

      const configurationCode = row.ConfigurationCode || row.configurationCode || ''
      const dealerId = this.$store.state.user.dealerId

      const encodedCode = encodeURIComponent(configurationCode)
      const path = `/b2c/vestiging/${dealerId}/configuratie/${encodedCode}`
      window.open(origin + path, '_blank')
    },
    confirmReplace(row, idx) {
      this.replaceConfirmRow = row
      this.replaceConfirmIdx = idx
    },
    async executeReplace() {
      const row = this.replaceConfirmRow
      const idx = this.replaceConfirmIdx
      const brand = row.Brand || row.brand || ''
      const commissionNumber = row.CommissionNumber || row.commissionNumber || ''
      const commissionNumberYear = row.CommissionYear || row.commissionYear || ''

      this.replacingIndex = idx
      this.replaceSuccess = null
      this.replaceError = null
      try {
        await adminApi.replaceVehicleWithBaseline(brand, commissionNumber, commissionNumberYear)
        this.replaceSuccess = `Voorraadauto ${commissionNumber} succesvol vervangen met de baseline.`
      } catch {
        this.replaceError =
          'Er is een fout opgetreden bij het vervangen van het voertuig met de baseline.'
      } finally {
        this.replacingIndex = null
        this.replaceConfirmRow = null
        this.replaceConfirmIdx = null
      }
    },
    onAccessoriesValueInput(event) {
      const raw = event.target.value
      this.accessoriesSearchValue =
        this.accessoriesSearchMode === 'stockId' ? raw.replace(/\D/g, '') : raw
    },
    async searchDealerAccessories() {
      if (!this.canSearchAccessories) return
      this.accessoriesLoading = true
      this.accessoriesError = null
      this.accessoriesResults = null
      this.accessoriesSearched = false
      try {
        if (this.accessoriesSearchMode === 'stockId') {
          const { data } = await adminApi.getStockInfo(this.accessoriesSearchValue)
          const stockId = data && data[0] && (data[0].StockId ?? data[0].stockId)
          if (!stockId) {
            this.accessoriesError = 'Vul een geldig komnummer in'
          } else {
            this.accessoriesResults = await DealerAccessories.getForAdminToolsStockId(
              this.accessoriesDealerId,
              stockId
            )
          }
        } else {
          this.accessoriesResults = await DealerAccessories.getForAdminToolsConfigurationCode(
            this.accessoriesDealerId,
            this.accessoriesSearchValue
          )
        }
        this.accessoriesSearched = true
      } catch (e) {
        if (this.accessoriesSearchMode === 'configurationCode' && e?.response?.status === 400) {
          this.accessoriesError = this.i18n.t('/configuration/prompt/infotext')
        } else {
          this.accessoriesError =
            'Er is een fout opgetreden bij het ophalen van de accessoires voor deze dealer.'
        }
      } finally {
        this.accessoriesLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only. The cards, rows, fields, buttons, alerts, segmented control and
// combobox come from the design system (styles/ds/). See styles/ds/README.md
// before adding anything here.

.tools-tab > .ds-card + .ds-card {
  margin-top: var(--ds-space-5);
}

.tools-tab__searchbar {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: var(--ds-space-4);

  .ds-form__field {
    max-width: 320px;
  }
}

.tools-tab__result-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  padding-top: 14px;
  margin-top: 6px;
  border-top: 1px solid var(--ds-color-border-subtle);
}

.tools-tab__stock-card {
  box-shadow: none;
}

.tools-tab__stock-actions {
  gap: var(--ds-space-2);
}

.tools-tab__replace-message {
  margin-top: var(--ds-space-3);
}

// Sunken toolbar holding dealer + search mode + value + Zoeken.
.tools-tab__accbar {
  display: flex;
  align-items: flex-end;
  gap: var(--ds-space-4);
  flex-wrap: wrap;
  margin-bottom: 18px;
  padding: 14px var(--ds-space-4);
  border: 1px solid var(--ds-color-border);
  border-radius: var(--ds-radius-md);
  background: var(--ds-color-surface-header);

  .ds-form__field {
    flex: 1;
    min-width: 170px;
  }
}

.tools-tab__accbar-combobox {
  min-width: 290px;
}

.tools-tab__accbar .tools-tab__accbar-mode {
  flex: 0 0 auto;
  min-width: 0;
}
</style>
